import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { getIdClaims } from "../auth/cognito";
import { MyWrestlers } from "../components/MyWrestlers";
import { TeamsHub } from "../components/TeamsHub";

export function Profile() {
  const { data, loading, error, reload } = useAsync(() => api.getMe(), []);
  const [params, setParams] = useSearchParams();

  // Feedback banner after returning from the Google Calendar OAuth callback.
  const [googleMsg, setGoogleMsg] = useState<
    { ok: boolean; text: string } | undefined
  >();
  useEffect(() => {
    const flag = params.get("googleLinked");
    if (flag === "true") {
      setGoogleMsg({ ok: true, text: "Google Calendar linked successfully." });
    } else if (flag === "false") {
      setGoogleMsg({
        ok: false,
        text: "Couldn't link Google Calendar. Please try again.",
      });
    }
    if (flag) {
      params.delete("googleLinked");
      setParams(params, { replace: true });
      reload(); // refresh googleLinked state from the server
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [optOut, setOptOut] = useState(false);
  const [calOptOut, setCalOptOut] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string>();
  const [saveErr, setSaveErr] = useState<string>();

  useEffect(() => {
    if (!data) return;
    setDisplayName(data.displayName ?? "");
    setPhone(data.phone ?? "");
    setOptOut(data.remindersOptOut ?? false);
    setCalOptOut(data.autoCalendarOptOut ?? false);
    setZip(data.homeZip ?? data.homeAddress?.zip ?? "");
  }, [data]);

  const save = async () => {
    setSaving(true);
    setSaveMsg(undefined);
    setSaveErr(undefined);
    try {
      const email = getIdClaims()?.email;
      await api.updateMe({
        displayName,
        phone,
        remindersOptOut: optOut,
        autoCalendarOptOut: calOptOut,
        ...(email ? { email } : {}),
        homeZip: zip.trim(),
      });
      setSaveMsg("Saved.");
      reload();
    } catch (e) {
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const linkGoogle = async () => {
    const { authUrl } = await api.googleAuthUrl();
    window.location.assign(authUrl);
  };
  const unlinkGoogle = async () => {
    await api.unlinkGoogle();
    reload();
  };

  if (loading) return <p>Loading…</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div>
      <h1>Profile</h1>

      <div className="card">
        <label>Display name</label>
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />

        <label>Phone (for SMS reminders)</label>
        <input
          value={phone}
          placeholder="+15551234567"
          onChange={(e) => setPhone(e.target.value)}
        />

        <label>Home ZIP code</label>
        <input
          value={zip}
          placeholder="e.g. 07102"
          inputMode="numeric"
          maxLength={5}
          style={{ maxWidth: "10rem" }}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
        />
        <p className="muted" style={{ marginTop: "0.25rem" }}>
          We use your ZIP only to show how far each meet is — both straight-line
          miles and estimated driving time. We never store your full address.
        </p>

        <label className="row" style={{ marginTop: "0.75rem" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={optOut}
            onChange={(e) => setOptOut(e.target.checked)}
          />
          <span>Opt out of reminder texts</span>
        </label>

        <div className="row" style={{ marginTop: "0.75rem" }}>
          <button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          {saveMsg && <span className="muted">{saveMsg}</span>}
          {saveErr && <span className="error">{saveErr}</span>}
        </div>
      </div>

      <MyWrestlers />

      {googleMsg && (
        <div className="card" style={{ borderColor: googleMsg.ok ? "var(--ok)" : "var(--danger)" }}>
          <p style={{ margin: 0, color: googleMsg.ok ? "var(--ok)" : "var(--danger)" }}>
            {googleMsg.ok ? "✅ " : "⚠️ "}
            {googleMsg.text}
          </p>
        </div>
      )}

      <div className="card">
        <div className="row spread">
          <div>
            <h2>Google Calendar</h2>
            <p className="muted">
              {data?.googleLinked
                ? "Linked — you can add meets to your calendar."
                : "Link your Google account to add meets to your calendar."}
            </p>
          </div>
          {data?.googleLinked ? (
            <button className="danger" onClick={unlinkGoogle}>
              Unlink
            </button>
          ) : (
            <button onClick={linkGoogle}>Link Google</button>
          )}
        </div>
        {data?.googleLinked && (
          <label className="row" style={{ marginTop: "0.75rem" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={!calOptOut}
              onChange={async (e) => {
                const next = !e.target.checked; // checked = auto-sync ON
                setCalOptOut(next);
                try {
                  await api.updateMe({ autoCalendarOptOut: next });
                  reload();
                } catch {
                  setCalOptOut(!next); // roll back on failure
                }
              }}
            />
            <span>
              Automatically add meets to my Google Calendar when I RSVP “Going”
            </span>
          </label>
        )}
      </div>

      <div className="card">
        <div className="row spread">
          <div>
            <h2>Match-results agent</h2>
            <p className="muted">
              Enable the local agent to append your wrestlers' match results
              (from your own FloWrestling / Wrestling Reimagined session) to
              their records. Required before running the agent.
            </p>
          </div>
          <label className="row" style={{ gap: "0.4rem" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={data?.agentConsent ?? false}
              onChange={async (e) => {
                try {
                  await api.updateMe({ agentConsent: e.target.checked });
                  reload();
                } catch {
                  /* reload keeps prior state on failure */
                }
              }}
            />
            <span>{data?.agentConsent ? "Enabled" : "Enable"}</span>
          </label>
        </div>
      </div>

      <TeamsHub />
    </div>
  );
}
